<template>
  <PrimaryPageContent class="pt-8">
    <v-row no-gutters>
      <v-col>
        <v-alert
          v-if="isBusinessInfoVerified"
          type="success"
          outlined
          text
        >
          Your business information is verified!
        </v-alert>
        <template v-else-if="isBusinessInfoSubmitted">
          <v-alert
            v-if="changeRequestMessage"
            type="warning"
            icon="mdi-help-circle-outline"
            outlined
            text
          >
            Our team recently checked your business information and requested a few changes. There is a message from team:
            <p>
              {{ changeRequestMessage }}
            </p>
          </v-alert>
          <v-alert
            v-else
            type="warning"
            icon="mdi-help-circle-outline"
            outlined
            text
          >
            Your business information was submitted to Riverflex team.<br>
            You will get email notification when they verify that.
          </v-alert>
        </template>
        <v-card
          class="mb-4 pa-6"
        >
          <div class="d-flex justify-space-between align-center mb-7">
            <div class="text-h5">
              Business details
            </div>
            <v-btn
              v-if="!editMode"
              rounded
              color="primary"
              light
              outlined
              :disabled="isUpdatingBusinessInfo"
              @click="handleEditClick"
            >
              Edit business information
            </v-btn>
          </div>
          <BusinessDetailsForm
            :initial-values="initialValues"
            :edit-mode="editMode"
            :loading="isUpdatingBusinessInfo"
            @submit="handleSubmit"
            @cancel="handleCancel"
          />
        </v-card>
      </v-col>
    </v-row>
  </PrimaryPageContent>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import PrimaryPageContent from '@/components/layouts/PrimaryPageContent.vue'
import BusinessDetailsForm, { FieldId, FormValues, defaultFormValues, fileFields } from '@/components/businessDetails/BusinessDetailsForm.vue'
import { IUser } from '@/types/models/user.model'
import { IBusinessInfo } from '@/types/models/businessInfo.model'
import { getFileNameFromUrl } from '@/utils/vuex'
import StorageAPI from '@/services/storage.service'

const fileUrlFields = ['passportFileUrl', 'professionalLiabilityInsuranceUrl', 'publicLiabilityInsuranceUrl'] as const
type FileUrlField = typeof fileUrlFields[number]

@Component({
  components: {
    PrimaryPageContent,
    BusinessDetailsForm,
  },
  computed: {
    ...mapGetters([
      'profile',
      'businessInfo',
      'isBusinessInfoVerified',
      'isBusinessInfoSubmitted',
    ]),
  }
})
export default class BusinessDetails extends Vue {
  @Prop() hasProfile: boolean

  // data
  initialValues: FormValues|null = null
  editMode: boolean = true
  isUpdatingBusinessInfo: boolean = false

  // computed
  profile: IUser
  businessInfo: IBusinessInfo
  isBusinessInfoVerified: boolean
  get isBusinessInfoSubmitted (): boolean {
    return !!this.profile.isBusinessInfoSet
  }

  get changeRequestMessage (): string|undefined {
    return this.businessInfo?.changeRequestMessage
  }

  @Watch('businessInfo')
  onBusinessInfoChange (info: IBusinessInfo) {
    this.prePopulateForm()
  }

  // lifecycle hooks
  async created () {
    await this.$store.dispatch('fetchBusinessInfo')
    if (this.isBusinessInfoSubmitted) {
      this.editMode = false
    }
    this.prePopulateForm()
  }

  // methods
  async handleSubmit (formData: FormValues) {
    const preparedData: {
      files?: Array<[string, File]>
      main: FormValues
    } = Object.entries(formData).reduce((acc: any, [key, value]) => {
      if (fileFields.includes(key) && value && value instanceof File) {
        return value.size > 0 ? ({ ...acc, files: [ ...(acc.files || []), [key, value]] }) : acc
      }
      return { ...acc, main: { ...acc.main, [key]: value } }
    }, {})

    this.isUpdatingBusinessInfo = true
    let fileS3Keys = {}
    if (preparedData.files) {
      fileS3Keys = await this.$store.dispatch('uploadBusinessInfoFilesToS3', preparedData.files)
    }
    const response = await this.$store.dispatch('updateBusinessInfo', { ...preparedData.main, ...fileS3Keys })
    this.isUpdatingBusinessInfo = false
    if (response) this.toggleEditMode(false)
  }

  handleCancel () {
    this.toggleEditMode(false)
  }

  handleEditClick () {
    this.toggleEditMode(!this.editMode)
  }

  prePopulateForm () {
    const initialValues: any = {}
    Object.entries(this.businessInfo).forEach(([key, value]) => {
      if (!defaultFormValues.hasOwnProperty(key)) return
      initialValues[key] = value
    })

    const fileFields: [FieldId, FileUrlField][] = [
      ['passport', 'passportFileUrl'],
      ['professionalLiabilityInsurance', 'professionalLiabilityInsuranceUrl'],
      ['publicLiabilityInsurance', 'publicLiabilityInsuranceUrl']
    ]
    fileFields.forEach(([formField, dataField]) => {
      if (!this.businessInfo[dataField]) return
      initialValues[formField] = this.businessInfo[dataField]
    })
    this.initialValues = initialValues
  }

  toggleEditMode (isEditMode: boolean) {
    this.editMode = isEditMode
  }
}
</script>
